import React, { useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAppSelector, useAppDispatch } from "../../store";
import { logout } from "../../features/auth/authSlice";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../context/ThemeContext";

interface MenuItem {
  title: string;
  path: string;
  icon: string;
  roles?: string[];
  children?: MenuItem[];
  badge?: string;
}

interface SidebarProps {
  collapsed?: boolean;
  isOpen?: boolean;
  isMobile?: boolean;
  onToggle?: (collapsed: boolean) => void;
  onClose?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  collapsed = false,
  isOpen = true,
  isMobile = false,
  onToggle,
  onClose,
}) => {
  const [openSubmenus, setOpenSubmenus] = useState<string[]>([]);
  const { user } = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { theme } = useTheme();
  const userRoles = user?.roles || [];
  const isDark = theme === "dark";

  const getMenuItems = (): MenuItem[] => {
    if (userRoles.includes("admin") || userRoles.includes("superadmin")) {
      return [
        {
          title: t("dashboard"),
          path: "/dashboard",
          icon: "bi bi-grid-1x2-fill",
        },
        {
          title: t("marketing_dashboard"),
          path: "/marketing-dashboard",
          icon: "bi bi-graph-up-arrow",
        },
        {
          title: t("companies"),
          path: "/companies",
          icon: "bi bi-buildings",
        },
        {
          title: t("users"),
          path: "/users",
          icon: "bi bi-people-fill",
        },
        {
          title: "Usage",
          path: "/usage",
          icon: "bi bi-bar-chart-line",
        },
        {
          title: t("ai_tools"),
          path: "/ai-tools",
          icon: "bi bi-cpu-fill",
        },
        {
          title: t("settings"),
          path: "/settings",
          icon: "bi bi-gear-fill",
        },
      ];
    }

    if (userRoles.includes("agency_owner")) {
      return [
        {
          title: t("dashboard"),
          path: "/dashboard",
          icon: "bi bi-grid-1x2-fill",
        },
        {
          title: t("marketing_dashboard"),
          path: "/marketing-dashboard",
          icon: "bi bi-graph-up-arrow",
        },
        {
          title: t("my_agency"),
          path: "/my-agency",
          icon: "bi bi-building-fill",
          children: [
            {
              title: t("agency_details"),
              path: "/my-agency/profile",
              icon: "bi bi-building-exclamation",
            },
            {
              title: t("client_companies"),
              path: "/client-companies",
              icon: "bi bi-buildings",
            },
            {
              title: t("my_team"),
              path: "/my-agency/my-team",
              icon: "bi bi-people-fill",
            },
          ],
        },
        {
          title: t("settings"),
          path: "/settings",
          icon: "bi bi-gear-fill",
        },
      ];
    }

    // Company Owner & Specialist
    if (userRoles.includes("company_owner") || userRoles.includes("company_specialist")) {
      return [
        {
          title: t("dashboard"),
          path: "/dashboard",
          icon: "bi bi-grid-1x2-fill",
        },
        ...(userRoles.includes("company_owner")
          ? [{
              title: t("my_company") || "My Company",
              path: "/my-company",
              icon: "bi bi-building-fill",
              children: [
                { title: t("company_details") || "Company Details", path: "/my-company/profile", icon: "bi bi-building-exclamation" },
                { title: t("my_team") || "My Team", path: "/my-company/my-team", icon: "bi bi-people-fill" },
              ]
            }]
          : []),
        {
          title: t("ai_tools"),
          path: "/ai-tools",
          icon: "bi bi-cpu-fill",
        },
      ];
    }

    // Marketing Specialist (default)
    return [
      {
        title: t("marketing_dashboard"),
        path: "/marketing-dashboard",
        icon: "bi bi-graph-up-arrow",
      },
    ];
  };

  const toggleSubmenu = (title: string) => {
    if (collapsed && !isMobile) return;

    setOpenSubmenus((prev) =>
      prev.includes(title)
        ? prev.filter((item) => item !== title)
        : [...prev, title]
    );
  };

  const handleToggle = () => {
    if (onToggle) {
      onToggle(!collapsed);
    }
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate("/login");
    if (onClose) onClose();
  };

  const handleLinkClick = () => {
    if (isMobile && onClose) {
      onClose();
    }
  };

  const isActiveRoute = (path: string) => {
    return (
      location.pathname === path || location.pathname.startsWith(path + "/")
    );
  };

  const menuItems = getMenuItems();
  const sidebarWidth = isMobile ? "260px" : collapsed ? "80px" : "260px";

  const sidebarStyle: React.CSSProperties = {
    width: sidebarWidth,
    background: isDark ? "rgba(26, 32, 44, 0.95)" : "rgba(255, 255, 255, 0.95)",
    borderRight: isDark
      ? "1px solid rgba(255, 255, 255, 0.08)"
      : "1px solid rgba(0, 0, 0, 0.08)",
    boxShadow: isDark
      ? "8px 0 32px rgba(0, 0, 0, 0.3)"
      : "8px 0 32px rgba(0, 0, 0, 0.08)",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    height: "100vh",
    position: "fixed",
    left: isMobile ? (isOpen ? "0" : "-280px") : "0",
    top: 0,
    zIndex: isMobile ? 1100 : 1000,
    backdropFilter: "blur(20px)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  };

  const logoStyle: React.CSSProperties = {
    color: isDark ? "#60a5fa" : "#3b82f6", // Solid renk
    fontWeight: "700",
    fontSize: collapsed && !isMobile ? "0" : "1.3rem",
    transition: "all 0.3s ease",
    overflow: "hidden",
    whiteSpace: "nowrap",
    textShadow: isDark
      ? "0 0 10px rgba(96, 165, 250, 0.3)"
      : "0 0 10px rgba(59, 130, 246, 0.3)", // Glow efekti
  };

  const navLinkStyle = (isActive: boolean): React.CSSProperties => ({
    display: "flex",
    alignItems: "center",
    padding: "12px 12px",
    margin: "2px 8px",
    borderRadius: "12px",
    textDecoration: "none",
    color: isActive ? "#ffffff" : isDark ? "#cbd5e1" : "#475569",
    background: isActive
      ? isDark
        ? "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)"
        : "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)"
      : "transparent",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    fontSize: "13px",
    fontWeight: isActive ? "600" : "500",
    boxShadow: isActive ? "0 4px 12px rgba(59, 130, 246, 0.25)" : "none",
    transform: isActive ? "translateX(2px)" : "translateX(0)",
    border: "none",
    width: "calc(100% - 16px)",
    cursor: "pointer",
    whiteSpace: "nowrap",
    overflow: "hidden",
  });

  const iconStyle: React.CSSProperties = {
    fontSize: "16px",
    minWidth: "18px",
    marginRight: collapsed && !isMobile ? "0" : "10px",
    transition: "all 0.3s ease",
    flexShrink: 0,
  };

  const badgeStyle: React.CSSProperties = {
    background: isDark
      ? "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)"
      : "linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)",
    color: "#ffffff",
    fontSize: "9px",
    fontWeight: "600",
    padding: "1px 4px",
    borderRadius: "4px",
    marginLeft: "auto",
    boxShadow: "0 1px 3px rgba(245, 158, 11, 0.3)",
  };

  const submenuStyle: React.CSSProperties = {
    background: isDark ? "rgba(255, 255, 255, 0.03)" : "rgba(0, 0, 0, 0.02)",
    borderRadius: "8px",
    margin: "2px 8px",
    padding: "4px 0",
    borderLeft: isDark
      ? "2px solid rgba(96, 165, 250, 0.4)"
      : "2px solid rgba(59, 130, 246, 0.3)",
    overflow: "hidden",
  };

  return (
    <div style={sidebarStyle}>
      {/* Header */}
      <div
        style={{
          padding: "16px 12px",
          borderBottom: isDark
            ? "1px solid rgba(255, 255, 255, 0.08)"
            : "1px solid rgba(0, 0, 0, 0.08)",
          display: "flex",
          alignItems: "center",
          justifyContent: collapsed && !isMobile ? "center" : "space-between",
          minHeight: "70px",
        }}
      >
        {!(collapsed && !isMobile) && (
          <div style={logoStyle}>AI Marketing 360</div>
        )}

        {!isMobile && (
          <button
            onClick={handleToggle}
            style={{
              background: isDark
                ? "rgba(255, 255, 255, 0.1)"
                : "rgba(0, 0, 0, 0.05)",
              border: "none",
              borderRadius: "12px",
              width: "36px",
              height: "36px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: isDark ? "#e2e8f0" : "#4a5568",
              cursor: "pointer",
              transition: "all 0.3s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = isDark
                ? "rgba(255, 255, 255, 0.15)"
                : "rgba(0, 0, 0, 0.1)";
              e.currentTarget.style.transform = "scale(1.1)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = isDark
                ? "rgba(255, 255, 255, 0.1)"
                : "rgba(0, 0, 0, 0.05)";
              e.currentTarget.style.transform = "scale(1)";
            }}
          >
            <i
              className={`bi ${collapsed ? "bi-chevron-right" : "bi-chevron-left"}`}
              style={{ fontSize: "14px" }}
            />
          </button>
        )}
      </div>

      {/* Navigation */}
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          padding: "12px 0",
        }}
        className="custom-scrollbar"
      >
        <nav>
          {menuItems.map((item) => (
            <div key={item.title}>
              {item.children ? (
                <>
                  {/* Parent with children */}
                  <div
                    onClick={() => toggleSubmenu(item.title)}
                    style={{
                      ...navLinkStyle(openSubmenus.includes(item.title)),
                      cursor: collapsed && !isMobile ? "default" : "pointer",
                    }}
                    onMouseEnter={(e) => {
                      if (!openSubmenus.includes(item.title)) {
                        e.currentTarget.style.background = isDark
                          ? "rgba(255, 255, 255, 0.05)"
                          : "rgba(0, 0, 0, 0.03)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!openSubmenus.includes(item.title)) {
                        e.currentTarget.style.background = "transparent";
                      }
                    }}
                  >
                    <i className={item.icon} style={iconStyle} />
                    {!(collapsed && !isMobile) && (
                      <>
                        <span style={{ flex: 1 }}>{item.title}</span>
                        {item.badge && (
                          <span style={badgeStyle}>{item.badge}</span>
                        )}
                        <i
                          className={`bi ${
                            openSubmenus.includes(item.title)
                              ? "bi-chevron-down"
                              : "bi-chevron-right"
                          }`}
                          style={{
                            fontSize: "12px",
                            marginLeft: "8px",
                            transition: "all 0.3s ease",
                          }}
                        />
                      </>
                    )}
                  </div>

                  {/* Submenu */}
                  {openSubmenus.includes(item.title) &&
                    !(collapsed && !isMobile) && (
                      <div style={submenuStyle}>
                        {item.children.map((child) => (
                          <NavLink
                            key={child.title}
                            to={child.path}
                            onClick={handleLinkClick}
                            style={({ isActive }) => ({
                              ...navLinkStyle(isActive),
                              margin: "1px 4px",
                              paddingLeft: "24px",
                              fontSize: "12px",
                            })}
                            onMouseEnter={(e) => {
                              if (!isActiveRoute(child.path)) {
                                e.currentTarget.style.background = isDark
                                  ? "rgba(255, 255, 255, 0.05)"
                                  : "rgba(0, 0, 0, 0.03)";
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isActiveRoute(child.path)) {
                                e.currentTarget.style.background =
                                  "transparent";
                              }
                            }}
                          >
                            <i className={child.icon} style={iconStyle} />
                            <span>{child.title}</span>
                          </NavLink>
                        ))}
                      </div>
                    )}
                </>
              ) : (
                // Regular menu item
                <NavLink
                  to={item.path}
                  onClick={handleLinkClick}
                  style={({ isActive }) => navLinkStyle(isActive)}
                  onMouseEnter={(e) => {
                    if (!isActiveRoute(item.path)) {
                      e.currentTarget.style.background = isDark
                        ? "rgba(255, 255, 255, 0.05)"
                        : "rgba(0, 0, 0, 0.03)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isActiveRoute(item.path)) {
                      e.currentTarget.style.background = "transparent";
                    }
                  }}
                >
                  <i className={item.icon} style={iconStyle} />
                  {!(collapsed && !isMobile) && (
                    <>
                      <span style={{ flex: 1 }}>{item.title}</span>
                      {item.badge && (
                        <span style={badgeStyle}>{item.badge}</span>
                      )}
                    </>
                  )}
                </NavLink>
              )}
            </div>
          ))}
        </nav>
      </div>

      {/* Footer */}
      <div
        style={{
          borderTop: isDark
            ? "1px solid rgba(255, 255, 255, 0.08)"
            : "1px solid rgba(0, 0, 0, 0.08)",
          padding: "12px",
        }}
      >
        {/* Profile */}
        <NavLink
          to="/profile"
          onClick={handleLinkClick}
          style={({ isActive }) => ({
            ...navLinkStyle(isActive),
            margin: "0 0 8px 0",
          })}
          onMouseEnter={(e) => {
            if (!isActiveRoute("/profile")) {
              e.currentTarget.style.background = isDark
                ? "rgba(255, 255, 255, 0.05)"
                : "rgba(0, 0, 0, 0.03)";
            }
          }}
          onMouseLeave={(e) => {
            if (!isActiveRoute("/profile")) {
              e.currentTarget.style.background = "transparent";
            }
          }}
        >
          <i className="bi bi-person-circle" style={iconStyle} />
          {!(collapsed && !isMobile) && <span>{t("my_profile")}</span>}
        </NavLink>

        {/* Logout */}
        <button
          onClick={handleLogout}
          style={{
            ...navLinkStyle(false),
            margin: "0",
            color: isDark ? "#f87171" : "#dc2626",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = isDark
              ? "rgba(248, 113, 113, 0.1)"
              : "rgba(220, 38, 38, 0.05)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "transparent";
          }}
        >
          <i className="bi bi-box-arrow-right" style={iconStyle} />
          {!(collapsed && !isMobile) && <span>{t("logout")}</span>}
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
